import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import useDeptDashboard from '../hooks/useDeptDashboard';
import MainLayout from '../components/layout/MainLayout';
import ComptabiliteModule from '../components/comptabilite/ComptabiliteModule';
import CRMModule from '../components/projets/CRMModule';
import PointageModule from '../components/shared/PointageModule';
import MarquerFinJournee from '../components/MarquerFinJournee';
import PersonnelManagementModule from '../components/shared/PersonnelManagementModule';
import { TaskManagementModule } from '../components/rh';
import MaPerformance from '../components/rh/MaPerformance';
import PersonalTaskModal from '../components/shared/PersonalTaskModal';
import TaskToggleWithPreuve from '../components/shared/TaskToggleWithPreuve';
import ProcesVerbauxCaisse from '../components/rh/ProcesVerbauxCaisse';
import ObjectifsEmployesModule from '../components/shared/ObjectifsEmployesModule';
import GenerationCodeModule from '../components/shared/GenerationCodeModule';
import AffichageCodeJour from '../components/shared/AffichageCodeJour';
import MesDemandesPermission from '../components/employe/MesDemandesPermission';
import { useMobileSidebar } from '../hooks/useMobileSidebar';
import api from '../services/api';
import './DashboardDGPage.css';

const DEPT_ID = 24; // ID du département Secrétariat - Caisse

export default function DashboardCaissePage() {
  const navigate = useNavigate();
  useMobileSidebar();
  const { logout: authLogout, user } = useAuth();
  
  const [activePage, setActivePage] = useState('vue-globale');
  const [loadingStats, setLoadingStats] = useState(true);
  const [showPersonalTaskModal, setShowPersonalTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formTask, setFormTask] = useState({ text: '', prio: 'normale', description: '' });
  const [statsData, setStatsData] = useState({
    soldeActuel: 0,
    encaissementsJour: 0,
    decaissementsJour: 0,
    operationsJour: 0,
    encaissementsSemaine: 0,
    decaissementsSemaine: 0,
    operationsSemaine: 0,
    sessionOuverte: false,
  });

  // Utiliser le hook useDeptDashboard pour gérer les employés et tâches
  const {
    employes,
    setEmployes,
    tasks,
    setTasks,
    loading,
    error,
    refetch,
    toggleTask,
    deleteTask,
    createTask,
    updateTask,
  } = useDeptDashboard({ deptId: DEPT_ID });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoadingStats(true);
      
      // Charger les statistiques de caisse
      const operationsResponse = await api.caisse.getAll();
      const operations = operationsResponse.operations || [];
      
      // Charger la session de caisse
      const sessionResponse = await api.caisse.getSessionCaisse();
      const sessionOuverte = sessionResponse.success && sessionResponse.session?.statut === 'ouverte';
      
      // Calculer les statistiques du jour
      const today = new Date().toISOString().split('T')[0];
      const operationsJour = operations.filter(op => op.date.startsWith(today));
      const encaissementsJour = operationsJour
        .filter(op => op.type === 'enc')
        .reduce((sum, op) => sum + parseFloat(op.montant || 0), 0);
      const decaissementsJour = operationsJour
        .filter(op => op.type === 'dec')
        .reduce((sum, op) => sum + parseFloat(op.montant || 0), 0);
      
      // Calculer les statistiques de la semaine
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
      weekStart.setHours(0, 0, 0, 0);
      const weekStartStr = weekStart.toISOString().split('T')[0];
      
      const operationsSemaine = operations.filter(op => op.date >= weekStartStr);
      const encaissementsSemaine = operationsSemaine
        .filter(op => op.type === 'enc')
        .reduce((sum, op) => sum + parseFloat(op.montant || 0), 0);
      const decaissementsSemaine = operationsSemaine
        .filter(op => op.type === 'dec')
        .reduce((sum, op) => sum + parseFloat(op.montant || 0), 0);
      
      // Calculer le solde actuel
      // 1. Récupérer la session actuelle
      const currentSessionResponse = await api.caisse.getSessionCaisse();
      const sessionCaisse = currentSessionResponse.session;
      
      // 2. Déterminer le solde de référence
      let soldeReference = 0;
      
      if (sessionCaisse && sessionCaisse.solde_ouverture) {
        // Si session ouverte, utiliser son solde d'ouverture
        soldeReference = parseFloat(sessionCaisse.solde_ouverture);
        console.log('📊 Solde référence (session ouverte):', soldeReference);
      } else {
        // Si session fermée, utiliser le solde de fermeture de la dernière session
        try {
          const histoResponse = await api.caisse.historiqueSessionsCaisse({ 
            statut: 'fermee',
            limit: 1 
          });
          
          if (histoResponse.success && histoResponse.sessions && histoResponse.sessions.length > 0) {
            soldeReference = parseFloat(histoResponse.sessions[0].solde_fermeture) || 0;
            console.log('📊 Solde référence (dernière session fermée):', soldeReference);
          } else {
            // Si pas de session fermée, utiliser le solde initial système
            const soldeInitialResponse = await api.caisse.getSoldeInitial();
            soldeReference = parseFloat(soldeInitialResponse.montant || 0);
            console.log('📊 Solde référence (solde initial système):', soldeReference);
          }
        } catch (error) {
          console.error('Erreur chargement dernier solde:', error);
          const soldeInitialResponse = await api.caisse.getSoldeInitial();
          soldeReference = parseFloat(soldeInitialResponse.montant || 0);
        }
      }
      
      // 3. Calculer le solde actuel = solde de référence + encaissements aujourd'hui - décaissements aujourd'hui
      const soldeActuel = soldeReference + encaissementsJour - decaissementsJour;
      
      setStatsData({
        soldeActuel,
        encaissementsJour,
        decaissementsJour,
        operationsJour: operationsJour.length,
        encaissementsSemaine,
        decaissementsSemaine,
        operationsSemaine: operationsSemaine.length,
        sessionOuverte,
      });
      
    } catch (error) {
      console.error('Erreur chargement données dashboard caisse:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleLogout = async () => {
    await authLogout();
    navigate('/auth', { replace: true });
  };

  const fmtN = (n) => Math.round(n).toLocaleString('fr-FR');

  // Calculer les statistiques pour l'équipe
  const presents = employes.filter((e) => e.present).length;
  const retards = employes.filter(
    (e) =>
      e.present &&
      e.arrivee &&
      e.arrivee !== '?' &&
      e.arrivee > (e.heureLimite || '08:30'),
  ).length;

  const scoreAvg =
    employes.length > 0
      ? (
          employes.reduce((s, e) => s + (e.score || 0), 0) / employes.length
        ).toFixed(1)
      : '0';

  // Filtrer les tâches du chef connecté pour "Mes tâches"
  const myTasks = useMemo(() => {
    const userId = user?.id;
    if (!userId) return tasks;
    return tasks.filter((t) => t.employee_id === userId);
  }, [tasks, user]);

  const myPendingTasks = myTasks.filter((t) => !t.done).length;

  const totalTasks = employes.reduce(
    (sum, emp) => sum + (emp.assignedTasks || []).length,
    0,
  );

  const pendingTasks = tasks.filter((t) => !t.done).length;

  // Gestion des tâches personnelles
  const handleOpenTaskModal = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormTask({
        text: task.titre || task.text,
        prio: task.priority || task.prio || 'normale',
        description: task.description || '',
      });
    } else {
      setEditingTask(null);
      setFormTask({ text: '', prio: 'normale', description: '' });
    }
    setShowPersonalTaskModal(true);
  };

  const handleSavePersonalTask = async () => {
    if (!formTask.text) {
      alert('Veuillez saisir l\'intitulé de la tâche');
      return;
    }

    try {
      const taskData = {
        titre: formTask.text,
        priority: formTask.prio,
        description: formTask.description,
        category: 'Secrétariat',
        departement_id: DEPT_ID,
        employee_id: user?.id,
      };

      if (editingTask) {
        await updateTask(editingTask.id, taskData);
      } else {
        await createTask(taskData);
      }

      setShowPersonalTaskModal(false);
      setEditingTask(null);
      setFormTask({ text: '', prio: 'normale', description: '' });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la tâche:', error);
      alert('Erreur lors de la sauvegarde de la tâche');
    }
  };

  return (
    <MainLayout>
      <div className="dashboard-dg-root">
        {/* TOPBAR */}
        <div className="topbar">
          <div className="topbar-left">
            <div
              className="tlogo"
              style={{ background: 'linear-gradient(135deg,#C9A84C,#8a6028)' }}
            >
              💵
            </div>
            <div>
              <div className="tname">FHC Groupe SARL</div>
              <div className="tsub">Dashboard Chef Département Secrétariat - Caisse</div>
            </div>
          </div>

          <div className="topbar-right">
            <div className="tdate">
              {new Date().toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </div>

            <button className="tbtn" onClick={() => { loadDashboardData(); refetch(); }}>
              🔄
            </button>

            <div style={{ textAlign: 'right' }}>
              <div className="tuser">{user?.nom_complet || 'Chef Caisse'}</div>
              <div className="trole" style={{ color: '#C9A84C' }}>
                💵 Secrétariat - Caisse
              </div>
            </div>

            <div
              className="tavatar"
              style={{ background: 'linear-gradient(135deg,#C9A84C,#8a6028)' }}
            >
              CA
            </div>

            <button
              className="tbtn"
              onClick={handleLogout}
              style={{ color: '#ef8080', borderColor: 'rgba(239,83,80,.3)' }}
            >
              🔹 Déconnexion
            </button>
          </div>
        </div>

        {/* LAYOUT */}
        <div className="layout">
          {/* SIDEBAR */}
          <div className="sidebar">
            <div className="sb-logo">Secrétariat & Caisse</div>

            {[
              { id: 'vue-globale', icon: '📊', label: 'Vue Globale' },
              { 
                id: 'personnel', 
                icon: '👥', 
                label: 'Personnel',
                badge: String(employes.length),
                badgeCls: 'green'
              },
              { 
                id: 'gestion-taches', 
                icon: '📋', 
                label: 'Gestion Tâches',
                badge: totalTasks > 0 ? String(totalTasks) : '',
                badgeCls: 'gold'
              },
              { 
                id: 'objectifs', 
                icon: '🎯', 
                label: 'Objectifs'
              },
              { 
                id: 'presence', 
                icon: '✅', 
                label: 'Présence & Pointage'
              },
              { 
                id: 'journal-caisse', 
                icon: '💵', 
                label: 'Journal de Caisse',
                badge: statsData.sessionOuverte ? 'OUVERTE' : '',
                badgeCls: statsData.sessionOuverte ? 'green' : ''
              },
              { 
                id: 'operations', 
                icon: '📝', 
                label: 'Opérations',
                badge: statsData.operationsJour > 0 ? String(statsData.operationsJour) : '',
                badgeCls: 'gold'
              },
              { id: 'rapports', icon: '📈', label: 'Rapports & Analyses' },
              { id: 'historique', icon: '📋', label: 'Historique' },
              { id: 'proces-verbaux', icon: '📋', label: 'Procès-Verbaux' },
              { id: 'crm', icon: '🤝', label: 'CRM' },
            ].map((it) => (
              <div
                key={it.id}
                className={`sb-item ${activePage === it.id ? 'active' : ''}`}
                onClick={() => setActivePage(it.id)}
              >
                <span className="si">{it.icon}</span>
                <span>{it.label}</span>
                {it.badge && (
                  <span className={`sb-badge ${it.badgeCls || ''}`}>
                    {it.badge}
                  </span>
                )}
              </div>
            ))}

            <div className="sb-sep" />

            <div className="sb-logo">Espace Personnel</div>

            <div
              className={`sb-item ${activePage === 'mes-taches' ? 'active' : ''}`}
              onClick={() => setActivePage('mes-taches')}
            >
              <span className="si">✅</span>
              <span>Mes Tâches</span>
              {myPendingTasks > 0 && (
                <span className="sb-badge">{myPendingTasks}</span>
              )}
            </div>

            <div
              className={`sb-item ${activePage === 'ma-performance' ? 'active' : ''}`}
              onClick={() => setActivePage('ma-performance')}
            >
              <span className="si">⭐</span>
              <span>Ma Performance</span>
            </div>

            <div
              className={`sb-item ${activePage === 'mes-permissions' ? 'active' : ''}`}
              onClick={() => setActivePage('mes-permissions')}
            >
              <span className="si">📝</span>
              <span>Mes Permissions</span>
            </div>

            <div className="sb-sep" />

            <div className="sb-item" onClick={() => navigate('/documents')}>
              <span className="si">📁</span><span>Documents</span>
            </div>
          </div>

          {/* MAIN */}
          <div className="main">
            {loading && (
              <div
                style={{
                  padding: 40,
                  textAlign: 'center',
                  color: '#C9A84C',
                  fontSize: 14,
                }}
              >
                Chargement des données du département...
              </div>
            )}

            {error && !loading && (
              <div
                style={{
                  margin: '12px 16px',
                  padding: '10px 14px',
                  background: 'rgba(239,83,80,.1)',
                  border: '1px solid rgba(239,83,80,.3)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#ef8080',
                }}
              >
                🔹 {error}
              </div>
            )}

            {/* VUE GLOBALE */}
            <div
              className={`page ${activePage === 'vue-globale' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">
                    Vue Globale • Secrétariat - Caisse
                  </div>
                  <div className="page-sub">
                    Tableau de bord en temps réel • {employes.length} collaborateurs
                  </div>
                </div>
              </div>

              {/* Pointage Module */}
              <PointageModule currentUser={user} />

              {/* Bouton Marquer Fin */}
              <MarquerFinJournee onSuccess={() => {
                console.log('Fin de journée enregistrée - Dashboard Caisse');
              }} />

              {/* KPIs Caisse */}
              <div className="card">
                <div className="card-title">💰 Statistiques Caisse</div>
                <div
                  className="kpi-grid"
                  style={{ gridTemplateColumns: 'repeat(4,1fr)' }}
                >
                  {[
                    {
                      icon: '💰',
                      val: fmtN(statsData.soldeActuel) + ' F',
                      label: 'Solde Actuel',
                      color: statsData.soldeActuel >= 0 ? '#66bb6a' : '#ef5350',
                    },
                    {
                      icon: '📥',
                      val: fmtN(statsData.encaissementsJour) + ' F',
                      label: "Encaissements aujourd'hui",
                      color: '#42a5f5',
                    },
                    {
                      icon: '📤',
                      val: fmtN(statsData.decaissementsJour) + ' F',
                      label: "Décaissements aujourd'hui",
                      color: '#ef5350',
                    },
                    {
                      icon: '📊',
                      val: statsData.operationsJour,
                      label: "Opérations aujourd'hui",
                      color: '#C9A84C',
                    },
                  ].map((k) => (
                    <div key={k.label} className="kpi-card">
                      <div
                        className="kpi-accent"
                        style={{ background: k.color }}
                      />
                      <div className="kpi-icon">{k.icon}</div>
                      <div className="kpi-val" style={{ color: k.color }}>
                        {k.val}
                      </div>
                      <div className="kpi-label">{k.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* KPIs Équipe */}
              <div className="card">
                <div className="card-title">👥 Équipe du Département</div>
                <div
                  className="kpi-grid"
                  style={{ gridTemplateColumns: 'repeat(4,1fr)' }}
                >
                  {[
                    {
                      icon: '👥',
                      val: employes.length,
                      label: 'Collaborateurs',
                      color: '#42a5f5',
                    },
                    {
                      icon: '✅',
                      val: presents,
                      label: 'Présents',
                      color: '#4caf50',
                    },
                    {
                      icon: '⏰',
                      val: retards,
                      label: 'Retards',
                      color: '#ff9800',
                    },
                    {
                      icon: '⭐',
                      val: scoreAvg,
                      label: 'Score moyen /10',
                      color: '#ab47bc',
                    },
                  ].map((k) => (
                    <div key={k.label} className="kpi-card">
                      <div
                        className="kpi-accent"
                        style={{ background: k.color }}
                      />
                      <div className="kpi-icon">{k.icon}</div>
                      <div className="kpi-val" style={{ color: k.color }}>
                        {k.val}
                      </div>
                      <div className="kpi-label">{k.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* État de la session */}
              <div className="card">
                <div className="card-title">🔐 État de la Session de Caisse</div>
                <div style={{ padding: '20px 0', textAlign: 'center' }}>
                  {statsData.sessionOuverte ? (
                    <div>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>🟢</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#66bb6a', marginBottom: 8 }}>
                        Session Ouverte
                      </div>
                      <div style={{ fontSize: 12, color: '#889' }}>
                        Vous pouvez enregistrer des opérations de caisse
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>🔴</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#ef5350', marginBottom: 8 }}>
                        Session Fermée
                      </div>
                      <div style={{ fontSize: 12, color: '#889' }}>
                        Ouvrez une session de caisse pour commencer les opérations
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Accès rapide */}
              <div className="card">
                <div className="card-title">⚡ Accès Rapide</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: '16px 0' }}>
                  <button
                    className="btn-gold"
                    onClick={() => setActivePage('journal-caisse')}
                    style={{ padding: '16px', fontSize: 13 }}
                  >
                    💵 Journal de Caisse
                  </button>
                  <button
                    className="btn-outline"
                    onClick={() => setActivePage('personnel')}
                    style={{ padding: '16px', fontSize: 13 }}
                  >
                    👥 Personnel
                  </button>
                  <button
                    className="btn-outline"
                    onClick={() => setActivePage('gestion-taches')}
                    style={{ padding: '16px', fontSize: 13 }}
                  >
                    📋 Gestion Tâches
                  </button>
                  <button
                    className="btn-outline"
                    onClick={() => setActivePage('rapports')}
                    style={{ padding: '16px', fontSize: 13 }}
                  >
                    📈 Rapports
                  </button>
                </div>
              </div>
            </div>

            {/* PERSONNEL */}
            <div
              className={`page ${activePage === 'personnel' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">Gestion du Personnel</div>
                  <div className="page-sub">
                    {employes.length} collaborateurs du département Secrétariat - Caisse
                  </div>
                </div>
              </div>
              <PersonnelManagementModule 
                employes={employes}
                setEmployes={setEmployes}
                deptId={DEPT_ID}
              />
            </div>

            {/* GESTION TÂCHES */}
            <div
              className={`page ${activePage === 'gestion-taches' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">Gestion des Tâches</div>
                  <div className="page-sub">
                    Gérer les tâches de l'équipe • {pendingTasks} tâches en attente
                  </div>
                </div>
              </div>
              <TaskManagementModule
                tasks={tasks}
                employes={employes}
                onToggleTask={toggleTask}
                onDeleteTask={deleteTask}
                onCreateTask={createTask}
                onUpdateTask={updateTask}
              />
            </div>

            {/* MES TÂCHES */}
            <div
              className={`page ${activePage === 'mes-taches' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">Mes Tâches Personnelles</div>
                  <div className="page-sub">
                    {myPendingTasks} tâches en attente sur {myTasks.length}
                  </div>
                </div>
                <button
                  className="btn-gold"
                  onClick={() => handleOpenTaskModal()}
                >
                  + Nouvelle tâche
                </button>
              </div>

              <div className="card">
                {myTasks.length === 0 ? (
                  <div
                    style={{
                      padding: '40px 0',
                      textAlign: 'center',
                      color: '#667',
                    }}
                  >
                    <div style={{ fontSize: 40, marginBottom: 10 }}>📝</div>
                    <div style={{ fontSize: 14, marginBottom: 5 }}>
                      Aucune tâche personnelle
                    </div>
                    <div style={{ fontSize: 11 }}>
                      Cliquez sur "+ Nouvelle tâche" pour créer votre première tâche
                    </div>
                  </div>
                ) : (
                  myTasks.map((t) => (
                    <div
                      key={t.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255,255,255,.04)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <TaskToggleWithPreuve
                        task={t}
                        onToggle={toggleTask}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13,
                            color: t.done ? '#667' : '#fff',
                            textDecoration: t.done ? 'line-through' : 'none',
                          }}
                        >
                          {t.titre || t.text}
                        </div>
                        {t.description && (
                          <div
                            style={{
                              fontSize: 11,
                              color: '#778',
                              marginTop: 3,
                            }}
                          >
                            {t.description}
                          </div>
                        )}
                      </div>
                      {(t.priority || t.prio) && (
                        <span
                          style={{
                            fontSize: 9,
                            padding: '2px 8px',
                            borderRadius: 99,
                            background:
                              (t.priority || t.prio) === 'haute'
                                ? 'rgba(239,83,80,.15)'
                                : (t.priority || t.prio) === 'normale'
                                ? 'rgba(201,168,76,.15)'
                                : 'rgba(102,187,106,.15)',
                            color:
                              (t.priority || t.prio) === 'haute'
                                ? '#ef5350'
                                : (t.priority || t.prio) === 'normale'
                                ? '#C9A84C'
                                : '#66bb6a',
                            fontWeight: 600,
                          }}
                        >
                          {(t.priority || t.prio) === 'haute'
                            ? '🔴 Haute'
                            : (t.priority || t.prio) === 'normale'
                            ? '🟡 Normale'
                            : '🟢 Basse'}
                        </span>
                      )}
                      <button
                        onClick={() => handleOpenTaskModal(t)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#C9A84C',
                          cursor: 'pointer',
                          fontSize: 14,
                          padding: '4px 8px',
                        }}
                        title="Modifier"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => deleteTask(t.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef5350',
                          cursor: 'pointer',
                          fontSize: 14,
                          padding: '4px 8px',
                        }}
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* MA PERFORMANCE */}
            <div
              className={`page ${activePage === 'ma-performance' ? 'active' : ''}`}
            >
              <MaPerformance />
            </div>

            {/* MES PERMISSIONS */}
            <div
              className={`page ${activePage === 'mes-permissions' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">📝 Mes Demandes de Permission</div>
                  <div className="page-sub">Gérez vos demandes de congés, absences et permissions</div>
                </div>
              </div>
              <MesDemandesPermission />
            </div>

            {/* OBJECTIFS */}
            <div
              className={`page ${activePage === 'objectifs' ? 'active' : ''}`}
            >
              <ObjectifsEmployesModule
                employes={employes}
                onRefresh={refetch}
              />
            </div>

            {/* PRÉSENCE & POINTAGE */}
            <div
              className={`page ${activePage === 'presence' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">✅ Présence & Pointage</div>
                  <div className="page-sub">Retards ? Absences ? Suivi</div>
                </div>
              </div>

              <div className="kpi-grid">
                <div className="kpi-card">
                  <div
                    className="kpi-accent"
                    style={{ background: '#4caf50' }}
                  />
                  <div className="kpi-icon">✅</div>
                  <div className="kpi-val" style={{ color: '#4caf50' }}>
                    {presents}
                  </div>
                  <div className="kpi-label">Présents</div>
                </div>

                <div className="kpi-card">
                  <div
                    className="kpi-accent"
                    style={{ background: '#ff9800' }}
                  />
                  <div className="kpi-icon">⏰</div>
                  <div className="kpi-val" style={{ color: '#ffb74d' }}>
                    {retards}
                  </div>
                  <div className="kpi-label">Retards</div>
                </div>

                <div className="kpi-card">
                  <div
                    className="kpi-accent"
                    style={{ background: '#ef5350' }}
                  />
                  <div className="kpi-icon">❌</div>
                  <div className="kpi-val" style={{ color: '#ef8080' }}>
                    {employes.filter((e) => !e.present).length}
                  </div>
                  <div className="kpi-label">Absents</div>
                </div>

                <div className="kpi-card">
                  <div
                    className="kpi-accent"
                    style={{ background: '#C9A84C' }}
                  />
                  <div className="kpi-icon">📊</div>
                  <div className="kpi-val" style={{ color: '#C9A84C' }}>
                    {employes.length > 0
                      ? Math.round((presents / employes.length) * 100)
                      : 0}
                    %
                  </div>
                  <div className="kpi-label">Taux présence</div>
                </div>
              </div>

              {/* Affichage du code de validation du jour */}
              <AffichageCodeJour />

              {/* Module de génération de code de pointage */}
              <GenerationCodeModule />

              <div className="card">
                <div className="card-title">✅ Présence du jour</div>

                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Collaborateur</th>
                      <th>Poste</th>
                      <th>Arrivée</th>
                      <th>Limite</th>
                      <th>Statut</th>
                    </tr>
                  </thead>

                  <tbody>
                    {employes.map((e) => {
                      const isRetard =
                        e.present &&
                        e.arrivee &&
                        e.arrivee !== '?' &&
                        e.arrivee > (e.heureLimite || '08:30');

                      return (
                        <tr key={e.id}>
                          <td>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  background: e.color + '22',
                                  color: e.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 9,
                                  fontWeight: 700,
                                }}
                              >
                                {e.av}
                              </div>
                              <span className="bold">
                                {e.prenom} {e.nom}
                              </span>
                            </div>
                          </td>

                          <td>{e.poste}</td>

                          <td>
                            {e.present ? (
                              <span style={{ color: isRetard ? '#ff9800' : '#4caf50' }}>
                                {e.arrivee || '?'}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>

                          <td>{e.heureLimite || '08:30'}</td>

                          <td>
                            {e.present ? (
                              isRetard ? (
                                <span
                                  style={{
                                    background: 'rgba(255,152,0,0.1)',
                                    color: '#ff9800',
                                    padding: '4px 8px',
                                    borderRadius: 6,
                                    fontSize: 10,
                                    fontWeight: 600,
                                  }}
                                >
                                  ⏰ Retard
                                </span>
                              ) : (
                                <span
                                  style={{
                                    background: 'rgba(76,175,80,0.1)',
                                    color: '#4caf50',
                                    padding: '4px 8px',
                                    borderRadius: 6,
                                    fontSize: 10,
                                    fontWeight: 600,
                                  }}
                                >
                                  ✅ Présent
                                </span>
                              )
                            ) : (
                              <span
                                style={{
                                  background: 'rgba(239,83,80,0.1)',
                                  color: '#ef5350',
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  fontSize: 10,
                                  fontWeight: 600,
                                }}
                              >
                                ❌ Absent
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* JOURNAL DE CAISSE */}
            <div
              className={`page ${activePage === 'journal-caisse' ? 'active' : ''}`}
            >
              <ComptabiliteModule 
                defaultTab="caisse" 
                hideOtherTabs={true}
                hideHeader={false}
              />
            </div>

            {/* OPERATIONS */}
            <div
              className={`page ${activePage === 'operations' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">Opérations de Caisse</div>
                  <div className="page-sub">
                    Liste des opérations enregistrées
                  </div>
                </div>
              </div>
              <ComptabiliteModule 
                defaultTab="caisse" 
                hideOtherTabs={true}
                hideHeader={true}
              />
            </div>

            {/* RAPPORTS */}
            <div
              className={`page ${activePage === 'rapports' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">Rapports & Analyses</div>
                  <div className="page-sub">
                    Analyses financières et export de données
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-title">📊 Rapports Disponibles</div>
                <div style={{ padding: '20px 0' }}>
                  <p style={{ fontSize: 12, color: '#889', marginBottom: 16 }}>
                    Utilisez le Journal de Caisse pour exporter les rapports en PDF ou Excel
                  </p>
                  <button
                    className="btn-gold"
                    onClick={() => setActivePage('journal-caisse')}
                  >
                    Accéder au Journal de Caisse
                  </button>
                </div>
              </div>
            </div>

            {/* HISTORIQUE */}
            <div
              className={`page ${activePage === 'historique' ? 'active' : ''}`}
            >
              <div className="page-header">
                <div>
                  <div className="page-title">Historique des Sessions</div>
                  <div className="page-sub">
                    Consultez l'historique des sessions de caisse
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-title">📋 Sessions de Caisse</div>
                <div style={{ padding: '20px 0', textAlign: 'center', color: '#889' }}>
                  <p style={{ fontSize: 12 }}>
                    L'historique des sessions est disponible dans le Journal de Caisse
                  </p>
                </div>
              </div>
            </div>

            {/* PROCES-VERBAUX */}
            <div
              className={`page ${activePage === 'proces-verbaux' ? 'active' : ''}`}
            >
              <ProcesVerbauxCaisse />
            </div>

            {/* CRM */}
            <div
              className={`page ${activePage === 'crm' ? 'active' : ''}`}
            >
              <CRMModule />
            </div>
          </div>
        </div>

        {/* Modal pour créer/modifier une tâche personnelle */}
        {showPersonalTaskModal && (
          <PersonalTaskModal
            formData={formTask}
            setFormData={setFormTask}
            onSave={handleSavePersonalTask}
            onClose={() => {
              setShowPersonalTaskModal(false);
              setEditingTask(null);
              setFormTask({ text: '', prio: 'normale', description: '' });
            }}
            isEditing={!!editingTask}
          />
        )}
      </div>
    </MainLayout>
  );
}
